import {
  InventoryOpsPoDetail,
  InventoryOpsReceivedSkuVariant,
  InventoryOpsRejectedSkuVariant,
  InventoryOpsSentSkuVariant,
  InventoryOpsSizeMix,
  InventoryOpsSkuVariantBase,
} from "../../../../../../models/ProjectManagement/ProjectInventoryProduct";
import { InventoryOpsSkuVariantLine } from "../../../../../../models/ProjectManagement/InventoryOpsRequests";
import { SkuQtyRow } from "./SkuVariantQtyList";
import { getPassedQtyBySku } from "./inventoryOpsQc";

export type InventoryOpsSkuSectionKey =
  | "rawInward"
  | "rawInwardQc"
  | "printOutward"
  | "printInward"
  | "printInwardQc";

const normalizeSku = (sku: string) => sku.trim().toLowerCase();

/** Collect variant_id by SKU from all recorded inventory ops arrays on the PO row. */
export const buildVariantIdBySkuMap = (
  po: InventoryOpsPoDetail | null | undefined,
): Map<string, number> => {
  const map = new Map<string, number>();
  if (!po) return map;

  const arrays: InventoryOpsSkuVariantBase[] = [
    ...(po.raw_inward_sku_variants || []),
    ...(po.raw_inward_qc_sku_variants || []),
    ...(po.print_outward_sku_variants || []),
    ...(po.print_inward_sku_variants || []),
    ...(po.print_inward_qc_sku_variants || []),
  ];

  arrays.forEach((item) => {
    if (item.sku && item.variant_id) {
      map.set(normalizeSku(item.sku), item.variant_id);
    }
  });

  return map;
};

const toQty = (...values: Array<number | null | undefined>): number => {
  for (const value of values) {
    if (value != null && !Number.isNaN(Number(value))) {
      return Number(value);
    }
  }
  return 0;
};

const getRecordedBySku = <T extends { sku: string }>(
  recordedVariants: T[] | null | undefined,
  getQty: (variant: T) => number | undefined,
): Map<string, number> => {
  const map = new Map<string, number>();
  (recordedVariants || []).forEach((variant) => {
    if (!variant.sku) return;
    map.set(normalizeSku(variant.sku), getQty(variant) ?? 0);
  });
  return map;
};

const getSkuSet = (
  variants: Array<{ sku: string }> | null | undefined,
): Set<string> => {
  const set = new Set<string>();
  (variants || []).forEach((variant) => {
    if (variant.sku) set.add(normalizeSku(variant.sku));
  });
  return set;
};

const resolveVariantId = (
  sku: string,
  sizeMixId: number | undefined,
  variantIdBySku: Map<string, number>,
): number => variantIdBySku.get(normalizeSku(sku)) ?? sizeMixId ?? 0;

const getReceivedQty = (variant: InventoryOpsReceivedSkuVariant) =>
  toQty(variant.total_received_quantity, variant.quantity_received);

const getRejectedQty = (
  variant: InventoryOpsRejectedSkuVariant & { quantity?: number },
) =>
  toQty(
    variant.total_rejected_quantity,
    variant.rejected_quantity,
    variant.quantity,
  );

const getSentQty = (variant: InventoryOpsSentSkuVariant) =>
  toQty(variant.total_quantity_sent, variant.quantity_sent);

type SkuStepContext = {
  sku: string;
  orderedQty: number;
  recordedQty: number;
  rawInwardReceived: number;
  rawInwardRejected: number;
  printOutwardSent: number;
  printInwardReceived: number;
  printInwardRejected: number;
  /** Raw QC passed qty; 0 when never QC'd, null on legacy (untracked) rows. */
  rawInwardPassed: number | null;
  /** Print QC passed qty; same null rule as rawInwardPassed. */
  printInwardPassed: number | null;
  hasRawQcRecord: boolean;
  hasPrintQcRecord: boolean;
  /** True when raw QC exists and is not older than the latest raw inward for this SKU. */
  hasFreshRawQc: boolean;
  /** True when print QC exists and is not older than the latest print inward for this SKU. */
  hasFreshPrintQc: boolean;
};

const toTimestamp = (value?: string | Date | null): number | null => {
  if (!value) return null;
  const time = new Date(value).getTime();
  return Number.isNaN(time) ? null : time;
};

const getLatestTimestampBySku = <
  T extends {
    sku: string;
    modified_date?: string | Date | null;
    created_date?: string | Date | null;
  },
>(
  variants: T[] | null | undefined,
): Map<string, number> => {
  const map = new Map<string, number>();
  (variants || []).forEach((variant) => {
    if (!variant.sku) return;
    const sku = normalizeSku(variant.sku);
    const ts =
      toTimestamp(variant.modified_date) ?? toTimestamp(variant.created_date);
    if (ts == null) return;
    const existing = map.get(sku);
    if (existing == null || ts > existing) {
      map.set(sku, ts);
    }
  });
  return map;
};

const hasFreshQcForSku = (
  sku: string,
  hasQcRecord: boolean,
  inwardTsBySku: Map<string, number>,
  qcTsBySku: Map<string, number>,
): boolean => {
  if (!hasQcRecord) return false;
  const inwardTs = inwardTsBySku.get(sku);
  if (inwardTs == null) return true;
  const qcTs = qcTsBySku.get(sku);
  if (qcTs == null) return false;
  return qcTs >= inwardTs;
};

/**
 * Build SKU rows from size mixes (ordered qty source), optionally filtered by
 * previous-section eligibility from inventory ops details.
 */
export const buildInventoryOpsSkuRows = (
  sizeMixes: InventoryOpsSizeMix[] | null | undefined,
  po: InventoryOpsPoDetail | null | undefined,
  recordedVariants: InventoryOpsSkuVariantBase[] | null | undefined,
  getRecordedQtyFn: (variant: InventoryOpsSkuVariantBase) => number | undefined,
  getRemainingQty: (context: SkuStepContext) => number,
  options?: {
    /** When set, only list SKUs that pass this check (previous section + size mix). */
    isEligible?: (context: SkuStepContext) => boolean;
    /** Hide remaining column (QC sections); keep maxQty for entry caps. */
    hideRemaining?: boolean;
    /**
     * Reference column (Ordered for raw inward, or prior-step qty for later
     * sections). Defaults to Ordered.
     */
    getReference?: (context: SkuStepContext) => {
      qty: number;
      label: string;
    };
    /** Extra per-row fields (e.g. QC passed / pending). */
    getExtra?: (
      context: SkuStepContext & { remainingQty: number },
    ) => Partial<SkuQtyRow>;
  },
): SkuQtyRow[] => {
  const variantIdBySku = buildVariantIdBySkuMap(po);

  const rawInwardReceivedBySku = getRecordedBySku(
    po?.raw_inward_sku_variants,
    getReceivedQty,
  );
  const rawInwardRejectedBySku = getRecordedBySku(
    po?.raw_inward_qc_sku_variants,
    getRejectedQty,
  );
  const printOutwardSentBySku = getRecordedBySku(
    po?.print_outward_sku_variants,
    getSentQty,
  );
  const printInwardReceivedBySku = getRecordedBySku(
    po?.print_inward_sku_variants,
    getReceivedQty,
  );
  const printInwardRejectedBySku = getRecordedBySku(
    po?.print_inward_qc_sku_variants,
    getRejectedQty,
  );
  const rawInwardPassedBySku = getPassedQtyBySku(
    po?.raw_inward_qc_sku_variants,
  );
  const printInwardPassedBySku = getPassedQtyBySku(
    po?.print_inward_qc_sku_variants,
  );
  const rawQcSkuSet = getSkuSet(po?.raw_inward_qc_sku_variants);
  const printQcSkuSet = getSkuSet(po?.print_inward_qc_sku_variants);
  const rawInwardTsBySku = getLatestTimestampBySku(po?.raw_inward_sku_variants);
  const rawQcTsBySku = getLatestTimestampBySku(po?.raw_inward_qc_sku_variants);
  const printInwardTsBySku = getLatestTimestampBySku(
    po?.print_inward_sku_variants,
  );
  const printQcTsBySku = getLatestTimestampBySku(
    po?.print_inward_qc_sku_variants,
  );
  const recordedBySku = getRecordedBySku(recordedVariants, getRecordedQtyFn);

  const sizeMixBySku = new Map<
    string,
    { sku: string; title?: string | null; quantity: number; id?: number }
  >();
  (sizeMixes || []).forEach((mix) => {
    if (!mix.sku) return;
    sizeMixBySku.set(normalizeSku(mix.sku), {
      sku: mix.sku,
      title: mix.title,
      quantity: mix.quantity ?? 0,
      id: mix.id,
    });
  });

  const buildRow = (
    sku: string,
    title: string | null | undefined,
    orderedQty: number,
    sizeMixId?: number,
  ): SkuQtyRow | null => {
    const normalized = normalizeSku(sku);
    const recordedQty = recordedBySku.get(normalized) ?? 0;
    const context: SkuStepContext = {
      sku,
      orderedQty,
      recordedQty,
      rawInwardReceived: rawInwardReceivedBySku.get(normalized) ?? 0,
      rawInwardRejected: rawInwardRejectedBySku.get(normalized) ?? 0,
      printOutwardSent: printOutwardSentBySku.get(normalized) ?? 0,
      printInwardReceived: printInwardReceivedBySku.get(normalized) ?? 0,
      printInwardRejected: printInwardRejectedBySku.get(normalized) ?? 0,
      rawInwardPassed: rawInwardPassedBySku.has(normalized)
        ? (rawInwardPassedBySku.get(normalized) ?? null)
        : 0,
      printInwardPassed: printInwardPassedBySku.has(normalized)
        ? (printInwardPassedBySku.get(normalized) ?? null)
        : 0,
      hasRawQcRecord: rawQcSkuSet.has(normalized),
      hasPrintQcRecord: printQcSkuSet.has(normalized),
      hasFreshRawQc: hasFreshQcForSku(
        normalized,
        rawQcSkuSet.has(normalized),
        rawInwardTsBySku,
        rawQcTsBySku,
      ),
      hasFreshPrintQc: hasFreshQcForSku(
        normalized,
        printQcSkuSet.has(normalized),
        printInwardTsBySku,
        printQcTsBySku,
      ),
    };

    if (options?.isEligible && !options.isEligible(context)) {
      return null;
    }

    const remainingQty = Math.max(0, getRemainingQty(context));
    const reference = options?.getReference?.(context) ?? {
      qty: orderedQty,
      label: "Ordered",
    };

    return {
      variant_id: resolveVariantId(sku, sizeMixId, variantIdBySku),
      sku,
      title: title ?? null,
      orderedQty,
      referenceQty: reference.qty,
      referenceLabel: reference.label,
      recordedQty,
      remainingQty: options?.hideRemaining ? undefined : remainingQty,
      maxQty: remainingQty,
      ...options?.getExtra?.({ ...context, remainingQty }),
    };
  };

  // Prefer size mixes as the catalog of SKUs; fall back to prior-step variants
  // when size mixes are missing.
  const candidates: Array<{
    sku: string;
    title?: string | null;
    quantity: number;
    id?: number;
  }> = sizeMixes?.length
    ? sizeMixes
        .filter((mix) => Boolean(mix.sku))
        .map((mix) => ({
          sku: mix.sku,
          title: mix.title,
          quantity: mix.quantity ?? 0,
          id: mix.id,
        }))
    : (recordedVariants || [])
        .filter((variant) => Boolean(variant.sku))
        .map((variant) => {
          const mix = sizeMixBySku.get(normalizeSku(variant.sku));
          return {
            sku: variant.sku,
            title: variant.title ?? mix?.title,
            quantity: mix?.quantity ?? 0,
            id: variant.variant_id ?? mix?.id,
          };
        });

  return candidates
    .map((item) => buildRow(item.sku, item.title, item.quantity, item.id))
    .filter((row): row is SkuQtyRow => row != null);
};

/**
 * Raw inward: list all size-mix variants.
 * Remaining = ordered − ((received − raw rejected) − print rejected).
 * Print QC rejects reopen raw so replacements can re-enter the full flow.
 */
export const getRawInwardSkuRows = (
  sizeMixes: InventoryOpsSizeMix[] | null | undefined,
  po: InventoryOpsPoDetail | null | undefined,
) =>
  buildInventoryOpsSkuRows(
    sizeMixes,
    po,
    po?.raw_inward_sku_variants,
    (variant) => getReceivedQty(variant as InventoryOpsReceivedSkuVariant),
    ({
      orderedQty,
      recordedQty,
      rawInwardRejected = 0,
      printInwardRejected = 0,
    }) => {
      const rawNet = Math.max(0, recordedQty - rawInwardRejected);
      const effectiveTowardOrder = Math.max(0, rawNet - printInwardRejected);
      return Math.max(0, orderedQty - effectiveTowardOrder);
    },
    {
      getReference: ({ orderedQty }) => ({
        qty: orderedQty,
        label: "Ordered",
      }),
    },
  );

/**
 * Raw QC: only variants that appear in raw inward (+ size mix).
 * Cap (passed + rejected in one save) = received − rejected − passed.
 * Legacy rows (passed untracked) keep cap = received − rejected; pending unknown.
 */
export const getRawInwardQcSkuRows = (
  sizeMixes: InventoryOpsSizeMix[] | null | undefined,
  po: InventoryOpsPoDetail | null | undefined,
) =>
  buildInventoryOpsSkuRows(
    sizeMixes,
    po,
    po?.raw_inward_qc_sku_variants,
    (variant) =>
      getRejectedQty(
        variant as InventoryOpsRejectedSkuVariant & { quantity?: number },
      ),
    ({ rawInwardReceived = 0, recordedQty, rawInwardPassed }) =>
      Math.max(0, rawInwardReceived - recordedQty - (rawInwardPassed ?? 0)),
    {
      isEligible: ({ rawInwardReceived }) => rawInwardReceived > 0,
      hideRemaining: true,
      getReference: ({ rawInwardReceived = 0 }) => ({
        qty: rawInwardReceived,
        label: "Received",
      }),
      getExtra: ({ rawInwardPassed, remainingQty }) => ({
        passedQty: rawInwardPassed,
        pendingQty: rawInwardPassed == null ? null : remainingQty,
      }),
    },
  );

/**
 * Raw QC when counting at QC (inward marked received, qty counted in QC):
 * every ordered SKU, tested capped at qty still to receive (same rule as raw
 * inward: ordered − ((received − raw rejected) − print rejected)).
 */
export const getRawInwardCountAtQcSkuRows = (
  sizeMixes: InventoryOpsSizeMix[] | null | undefined,
  po: InventoryOpsPoDetail | null | undefined,
) =>
  buildInventoryOpsSkuRows(
    sizeMixes,
    po,
    po?.raw_inward_qc_sku_variants,
    (variant) =>
      getRejectedQty(
        variant as InventoryOpsRejectedSkuVariant & { quantity?: number },
      ),
    ({ orderedQty, rawInwardReceived, recordedQty, printInwardRejected }) => {
      const rawNet = Math.max(0, rawInwardReceived - recordedQty);
      const effectiveTowardOrder = Math.max(0, rawNet - printInwardRejected);
      return Math.max(0, orderedQty - effectiveTowardOrder);
    },
    {
      hideRemaining: true,
      getReference: ({ rawInwardReceived = 0 }) => ({
        qty: rawInwardReceived,
        label: "Received",
      }),
      getExtra: ({ rawInwardPassed, remainingQty }) => ({
        passedQty: rawInwardPassed,
        pendingQty: remainingQty,
      }),
    },
  );

/** Raw QC approved qty: passed when tracked, else received − rejected. */
const getRawApprovedQty = ({
  rawInwardReceived = 0,
  rawInwardRejected = 0,
  rawInwardPassed,
}: SkuStepContext) =>
  rawInwardPassed != null
    ? rawInwardPassed
    : Math.max(0, rawInwardReceived - rawInwardRejected);

/**
 * Print outward: size mix ∩ raw inward ∩ raw QC approved qty.
 * approved = QC passed when tracked, else (legacy) received − rejected;
 * remaining = approved − already sent.
 * Lists SKUs that were inwarded, QC'd, and still have approved qty (or already sent).
 */
export const getPrintOutwardSkuRows = (
  sizeMixes: InventoryOpsSizeMix[] | null | undefined,
  po: InventoryOpsPoDetail | null | undefined,
) =>
  buildInventoryOpsSkuRows(
    sizeMixes,
    po,
    po?.print_outward_sku_variants,
    (variant) => getSentQty(variant as InventoryOpsSentSkuVariant),
    (context) => Math.max(0, getRawApprovedQty(context) - context.recordedQty),
    {
      isEligible: (context) => {
        const {
          rawInwardReceived,
          rawInwardPassed,
          printOutwardSent,
          hasFreshRawQc,
        } = context;
        if (rawInwardReceived <= 0) return false;
        // Legacy QC: fresh QC required to send newly approved qty. Tracked QC:
        // only passed qty is sendable. Keep already-sent rows visible.
        if (rawInwardPassed == null && !hasFreshRawQc && printOutwardSent <= 0)
          return false;
        return getRawApprovedQty(context) > 0 || printOutwardSent > 0;
      },
      getReference: (context) => ({
        qty: getRawApprovedQty(context),
        label: context.rawInwardPassed != null ? "QC Passed" : "Raw Inward",
      }),
    },
  );

/**
 * Print inward: every SKU that was print-outwarded (size mix ∩ sent variants).
 * A later raw QC must not hide already-sent SKUs — those units are still at print.
 * remaining = sent − already print-inward received.
 */
export const getPrintInwardSkuRows = (
  sizeMixes: InventoryOpsSizeMix[] | null | undefined,
  po: InventoryOpsPoDetail | null | undefined,
) =>
  buildInventoryOpsSkuRows(
    sizeMixes,
    po,
    po?.print_inward_sku_variants,
    (variant) => getReceivedQty(variant as InventoryOpsReceivedSkuVariant),
    ({ printOutwardSent = 0, recordedQty }) =>
      Math.max(0, printOutwardSent - recordedQty),
    {
      isEligible: ({ printOutwardSent }) => printOutwardSent > 0,
      getReference: ({ printOutwardSent = 0 }) => ({
        qty: printOutwardSent,
        label: "Print Outward",
      }),
    },
  );

/**
 * Print QC: only variants that were print-inwarded (+ size mix).
 * Cap (passed + rejected in one save) = received − rejected − passed.
 */
export const getPrintInwardQcSkuRows = (
  sizeMixes: InventoryOpsSizeMix[] | null | undefined,
  po: InventoryOpsPoDetail | null | undefined,
) =>
  buildInventoryOpsSkuRows(
    sizeMixes,
    po,
    po?.print_inward_qc_sku_variants,
    (variant) =>
      getRejectedQty(
        variant as InventoryOpsRejectedSkuVariant & { quantity?: number },
      ),
    ({ printInwardReceived = 0, recordedQty, printInwardPassed }) =>
      Math.max(0, printInwardReceived - recordedQty - (printInwardPassed ?? 0)),
    {
      isEligible: ({ printInwardReceived }) => printInwardReceived > 0,
      hideRemaining: true,
      getReference: ({ printInwardReceived = 0 }) => ({
        qty: printInwardReceived,
        label: "Received",
      }),
      getExtra: ({ printInwardPassed, remainingQty }) => ({
        passedQty: printInwardPassed,
        pendingQty: printInwardPassed == null ? null : remainingQty,
      }),
    },
  );

/**
 * Read-only QC rows (all zeros) from size mixes, shown before the inward
 * exists so the SKU table is visible instead of product-level inputs.
 */
export const getQcPlaceholderSkuRows = (
  sizeMixes: InventoryOpsSizeMix[] | null | undefined,
): SkuQtyRow[] =>
  (sizeMixes || [])
    .filter((mix) => Boolean(mix.sku))
    .map((mix) => ({
      variant_id: mix.id,
      sku: mix.sku,
      title: mix.title ?? null,
      orderedQty: mix.quantity ?? 0,
      referenceQty: 0,
      referenceLabel: "Received",
      recordedQty: 0,
      maxQty: 0,
      passedQty: 0,
      pendingQty: 0,
    }));

/**
 * Print QC when counting at QC: every SKU sent to the printer, tested capped
 * at what can still come back (sent − (received − print QC rejected)).
 */
export const getPrintInwardCountAtQcSkuRows = (
  sizeMixes: InventoryOpsSizeMix[] | null | undefined,
  po: InventoryOpsPoDetail | null | undefined,
) =>
  buildInventoryOpsSkuRows(
    sizeMixes,
    po,
    po?.print_inward_qc_sku_variants,
    (variant) =>
      getRejectedQty(
        variant as InventoryOpsRejectedSkuVariant & { quantity?: number },
      ),
    ({ printOutwardSent, printInwardReceived, recordedQty }) =>
      Math.max(
        0,
        printOutwardSent - Math.max(0, printInwardReceived - recordedQty),
      ),
    {
      isEligible: ({ printOutwardSent }) => printOutwardSent > 0,
      hideRemaining: true,
      getReference: ({ printInwardReceived = 0 }) => ({
        qty: printInwardReceived,
        label: "Received",
      }),
      getExtra: ({ printInwardPassed, remainingQty }) => ({
        passedQty: printInwardPassed,
        pendingQty: remainingQty,
      }),
    },
  );

const findSkuLine = (
  row: SkuQtyRow,
  skuLines: InventoryOpsSkuVariantLine[] | null | undefined,
) =>
  skuLines?.find(
    (line) => line.sku === row.sku || line.variant_id === row.variant_id,
  );

export const buildSelectedSkuLines = (
  rows: SkuQtyRow[],
  skuLines: InventoryOpsSkuVariantLine[] | null | undefined,
  selectedSkus: Set<string>,
  options?: {
    allowZero?: boolean;
    useRemaining?: boolean;
    /** Force quantity for every selected SKU (e.g. QC Mark All Complete = 0 rejection). */
    forceQuantity?: number;
  },
): InventoryOpsSkuVariantLine[] =>
  rows
    .filter((row) => selectedSkus.has(row.sku))
    .map((row) => {
      const entered = findSkuLine(row, skuLines);
      const quantity =
        options?.forceQuantity !== undefined
          ? options.forceQuantity
          : options?.useRemaining
            ? (row.remainingQty ?? row.maxQty ?? 0)
            : (entered?.quantity ?? 0);

      return {
        variant_id: row.variant_id,
        sku: row.sku,
        title: row.title ?? null,
        quantity,
      };
    })
    .filter((line) => options?.allowZero || line.quantity > 0);

export const hasSelectedSkus = (selectedSkus: Set<string>): boolean =>
  selectedSkus.size > 0;

export const hasEnteredQtyForSelectedSkus = (
  rows: SkuQtyRow[],
  skuLines: InventoryOpsSkuVariantLine[] | null | undefined,
  selectedSkus: Set<string>,
  options?: { allowZero?: boolean },
): boolean => {
  if (selectedSkus.size === 0) return false;

  return rows
    .filter((row) => selectedSkus.has(row.sku))
    .some((row) => {
      const line = findSkuLine(row, skuLines);
      if (!line) return false;
      return options?.allowZero ? line.quantity >= 0 : line.quantity > 0;
    });
};

export const hasSelectedPositiveQty = (
  lines: InventoryOpsSkuVariantLine[],
): boolean => lines.some((line) => line.quantity > 0);
